import React, {useState} from "react";
import styled from "styled-components";
import {
  Button as MuiButton,
  Card,
  CardContent,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
} from "@mui/material";
import { Description, Delete } from '@mui/icons-material';
import Dropzone from 'react-dropzone';
import MuiAlert from '@mui/material/Alert';
import {spacing} from "@mui/system";
import PropTypes from "prop-types";
import {useDispatch} from "react-redux";
import {getByUuid as getPropertyByUuid, updateSitePlan, uploadSitePlan} from "../api/actions";
import CircularProgress from "@mui/material/CircularProgress";

const Button = styled(MuiButton)(spacing);


const SitePlanUpload = ({
                          property,
                          isLoading = false,
                          hasExistingSitePlan = false,
                          onClose
                        }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [showOverwriteDialog, setShowOverwriteDialog] = useState(false);
  const [isUploading, setIsUploading] = useState(false);


  const dispatch = useDispatch();

  const onDrop = (file) => {
    setSelectedFile(file[0]);
  }

  const onCancel = () => {
    setSelectedFile(null);
  }

  const onSave = async (file) => {
    if (hasExistingSitePlan){
      setShowOverwriteDialog(true);
    } else {
      await handleUpload(file)
    }
  }

  const handleUpload = async (file) => {
    if (!file) return;
    if (!isLoading && !property.uuid) return;

      setIsUploading(true);
      try {
        await dispatch(uploadSitePlan(property.uuid, file));
        const s3Url = property.sitePlans
        if (s3Url) {
          await dispatch(updateSitePlan(property.uuid, s3Url));
        }
        await dispatch(getPropertyByUuid(property.uuid));
        setSelectedFile(null);
        onClose();
      } catch (error) {
        console.log(error, 'error uploading site plans');
      } finally {
        setIsUploading(false);
      }
  };

  const handleOverwriteConfirm = async () => {
    setShowOverwriteDialog(false);
    await handleUpload(selectedFile);
  };

  const handleOverwriteCancel = () => {
    setShowOverwriteDialog(false);
    setSelectedFile(null);
  };

  return (
    <Card>
      <CardContent>
        <Grid container spacing={2}>
          <Grid container item spacing={2}>
            <Grid container spacing={4}>
              <Grid
                item
                xs={12}
                className="d-flex justify-content-center align-items-center">
                <div className="w-100">
                  {hasExistingSitePlan && (
                    <MuiAlert
                      className="mb-4"
                      severity="warning"
                    >
                      The <a href={property?.sitePlanSignedUrl} target={"_blank"}>Site Plan File</a> already exists for this
                      property.
                    </MuiAlert>
                  )}
                  {!selectedFile && !hasExistingSitePlan && (
                    <MuiAlert severity="success">
                      Files uploaded will appear here!
                    </MuiAlert>
                  )}
                  {selectedFile && (
                    <div
                      className="card card-box btn rounded text-left mb-4 p-3">
                      <div className="d-flex align-items-center flex-column flex-sm-row justify-content-between">
                        <div>
                          <div className="text-center text-first display-2 d-50 rounded-circle mb-3 mb-sm-2">
                            <Description fontSize="small" />
                          </div>
                        </div>
                        <div className="pl-0 pl-sm-3">
                          <div className="d-block text-center d-sm-flex align-items-center">
                            <span className="font-size-md">{selectedFile?.name}</span>
                          </div>
                          <p className="text-black-50 opacity-8 mb-0">
                            <b>{selectedFile?.size} bytes</b>
                          </p>
                        </div>
                        <div>
                          <div className="text-center text-first display-2 d-50 rounded-circle mb-3 mb-sm-2">
                            <Fab
                              onClick={() => onCancel()}
                              size="small"
                              color="primary">
                              <Delete />
                            </Fab>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Grid>
              <Grid item xs={12}>
                <div className="dropzone">
                  <Dropzone
                    fullWidth
                    onDrop={onDrop}
                    onFileDialogCancel={onCancel}>
                    {({getRootProps, getInputProps, isDragActive}) => (
                      <div {...getRootProps()}>
                        <input {...getInputProps()} />
                        <div className="dz-message">
                          <div className="dx-text">
                            {isDragActive
                              ? 'Drop the PDF file here...'
                              : 'Drag and drop a PDF file here, or click to select'
                            }
                          </div>
                        </div>
                      </div>
                    )}
                  </Dropzone>
                </div>
              </Grid>
            </Grid>
          </Grid>

          <Grid container item spacing={2} justifyContent={'flex-end'}>
            <Grid item>
              <Button
                onClick={onCancel}
                color="primary"
                disabled={!selectedFile}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                onClick={() => onSave(selectedFile)}
                color="secondary"
                disabled={!selectedFile || isUploading}
              >
                {isUploading ? <CircularProgress size={20} color="inherit" /> : 'Save'}
              </Button>
            </Grid>
          </Grid>
        </Grid>

        <Dialog
          open={showOverwriteDialog}
          onClose={handleOverwriteCancel}
          aria-labelledby="overwrite-dialog-title"
          aria-describedby="overwrite-dialog-description"
        >
          <DialogTitle id="overwrite-dialog-title">
            Overwrite Existing Site Plan?
          </DialogTitle>
          <DialogContent>
            <Typography>
              Uploading a new site plan will overwrite the existing one. Do you want to continue?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleOverwriteCancel} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleOverwriteConfirm}
              color="secondary"
              variant="contained"
              disabled={isUploading}
            >
              {isUploading ? 'Uploading...' : 'Continue'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

SitePlanUpload.propTypes = {
  property: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  hasExistingSitePlan: PropTypes.bool.isRequired,
};

export default SitePlanUpload; 